import { CardState } from '@shared-models';

// Payment states only ever apply to Cartera (portfolio/collections) cards —
// Sales cards use order/delivery/invoice-without-notes/invoice-with-notes/
// rejected and never these. Shared so card-body and card-header stay in
// sync on what counts as a Cartera card instead of each keeping its own copy.
const PORTFOLIO_CARD_STATES: readonly CardState[] = [
  'invoice-unpaid',
  'invoice-partial',
  'invoice-fully-paid',
  'invoice-voided',
];

export const isPortfolioCardState = (state: CardState): boolean =>
  PORTFOLIO_CARD_STATES.includes(state);

// Single source of truth for "this invoice is voided" (Anulada): the
// notes-adjusted total is effectively zero and no real payment was ever
// recorded against it. Shared so Cartera's card-state derivation and the
// payments modal's invoice-detail tab agree on the same rule instead of
// each re-deriving it.
export const INVOICE_BALANCE_EPSILON = 0.01;

export const isInvoiceVoided = (
  adjustedTotal: number,
  hasRealPayment: boolean,
): boolean => adjustedTotal <= INVOICE_BALANCE_EPSILON && !hasRealPayment;

// PayInvoice.status lifecycle: 1 = created/pending approval, 2 = approved by
// vendor (p_vendor), 3 = approved and transferred. Only status 3 is money
// actually applied to the invoice — the same rule the backend's
// calculateInvoiceBalance(onlyAppliedPayments: true) uses to decide when a
// factura's estado_factura flips to PAID (see recalcInvoiceStatus.js).
// Shared here so Cartera's card state, the notes-detail modal's balance, and
// the "Registrar pago" modal's status tile can't drift apart on what counts
// as a real payment, the way they did before this constant existed.
export const APPLIED_PAYMENT_STATUS = 3;

export const sumAppliedPayments = (
  payments: readonly { status: number; value: number }[] | null | undefined,
): number =>
  (payments ?? [])
    .filter((payment) => payment.status === APPLIED_PAYMENT_STATUS)
    .reduce((sum, payment) => sum + (payment.value || 0), 0);

// The complement of sumAppliedPayments: money that's been registered against
// the invoice but hasn't cleared Admin approval yet (status 1 or 2). Kept as
// its own function — rather than derived as total-minus-applied by callers —
// so every screen that wants to show "pending approval" separately from
// "applied" reads it the same way.
export const sumPendingApprovalPayments = (
  payments: readonly { status: number; value: number }[] | null | undefined,
): number =>
  (payments ?? [])
    .filter((payment) => payment.status !== APPLIED_PAYMENT_STATUS)
    .reduce((sum, payment) => sum + (payment.value || 0), 0);

/**
 * The payment state stored in `factura.estado_factura` and
 * `purchase_invoice.state_invoice`. Mirrors the backend's
 * resolveDocumentState — the server decides the state now, the card only
 * renders it.
 */
export const DOCUMENT_STATE = {
  VOID: 0,
  UNPAID: 10,
  PARTIAL: 20,
  PAID: 30,
} as const;

const CARD_STATE_BY_DOCUMENT_STATE: Record<number, CardState> = {
  [DOCUMENT_STATE.VOID]: 'invoice-voided',
  [DOCUMENT_STATE.UNPAID]: 'invoice-unpaid',
  [DOCUMENT_STATE.PARTIAL]: 'invoice-partial',
  [DOCUMENT_STATE.PAID]: 'invoice-fully-paid',
};

const DOCUMENT_STATE_BY_CARD_STATE: Partial<Record<CardState, number>> = {
  'invoice-voided': DOCUMENT_STATE.VOID,
  'invoice-unpaid': DOCUMENT_STATE.UNPAID,
  'invoice-partial': DOCUMENT_STATE.PARTIAL,
  'invoice-fully-paid': DOCUMENT_STATE.PAID,
};

export const cardStateFromDocumentState = (
  state: number | null | undefined,
): CardState =>
  CARD_STATE_BY_DOCUMENT_STATE[state ?? DOCUMENT_STATE.UNPAID] ??
  'invoice-unpaid';

/** Card states the user picked, as the numbers the listing filters on. */
export const documentStatesFromCardStates = (states: string[]): number[] =>
  states
    .map((state) => DOCUMENT_STATE_BY_CARD_STATE[state as CardState])
    .filter((state): state is number => state !== undefined);
